/**
 * @author Angel Labrada
 * @since v0.0.1
 * @date 21/12/21
 */
import React, {memo, useEffect, useCallback, useMemo} from 'react';
import PropTypes from 'prop-types';
import {List, Typography, Button} from 'antd';
import classNames from 'classnames';
import {useInfinite} from '@/utils/createInfiniteHook';
import { useInfiniteScroll } from '@dofleini/use-scroll';
import TemplatesApiService from '@/modules/templates/services/TemplatesApiService';
import {useFilter} from '@/contexts/FilterContext';
import {/*useHistory, */useParams} from 'react-router-dom';
import moment from 'moment';
import {useTranslation} from 'react-i18next';
import {useAuditWizard} from '@/modules/auditorAudits/hooks/useWizard';
import { Scrollbars } from 'react-custom-scrollbars';
import styles from './index.less';
import {stringToHslColor} from '@/utils/utils';
import {CACHE_KEY_LIST_TEMPLATES} from '@/modules/templates/contants/templates';
import {useClientFilter} from '@/hooks/useClientFilter';
import {InFilter, TermFilter, OperatorFilter} from '@dofleini/query-builder';
const SERVER_PAGE = 25;

const TemplateList = ({currentCategory, value, onChange}) => {
  const {globalFilter, setGlobalFilter} = useFilter();
  const {currentService, servicesTemplatesGoal} = useClientFilter();

  const templatesFilter = useMemo(() => {
    const clientSelectedServiceIndex = servicesTemplatesGoal?.findIndex((item) => {
      return item?.service === currentService;
    });
    if (clientSelectedServiceIndex !== -1) {
      const templates = servicesTemplatesGoal[clientSelectedServiceIndex]?.templates;
      const baseFilter = new InFilter({field: '_id', value: templates}).toQuery();
      const enabledFilter = new TermFilter({field: 'enabled', value: {$ne: false}}).toQuery();
      return new OperatorFilter({type: 'AND', filters: [baseFilter, enabledFilter]}).toQuery();
    }
    return new InFilter({field: '_id', value: []}).toQuery();
  }, [currentService, servicesTemplatesGoal]);

  useEffect(() => {
    setGlobalFilter(templatesFilter);
  }, [templatesFilter, setGlobalFilter]);

  const {t} = useTranslation('auditorAudits');
  const { currentClient } = useClientFilter();
  const {id} = useParams();

  const {templateId, setTemplateId, setTemplateCategories, serviceId, setServiceId} = useAuditWizard();


  useEffect(() => {
    if (value?.parent && id)
      setTemplateId(value?.parent);
  }, [id, setTemplateId, value?.parent]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
    isSuccess,
    onLoadCompleted
  } = useInfinite({
    key: [CACHE_KEY_LIST_TEMPLATES, globalFilter, currentCategory],
    service: TemplatesApiService.search,
    filters: globalFilter,
    queryConfig: { size: Math.max(25, SERVER_PAGE), enabled: !!(globalFilter && currentClient?._id) },
    additionalParams: {client: currentClient?._id}
  });

  useInfiniteScroll({
    fromWindow: false,
    hasNext: hasNextPage && !(isFetching || isLoading),
    onLoadMore: fetchNextPage
  });

  useEffect(() => {
    if (isSuccess)
      onLoadCompleted && onLoadCompleted(data);
  }, [data, isSuccess, onLoadCompleted]);

  useEffect(() => {
    if(data?.length > 0) {
      setTemplateCategories(data?.map(item => item?.category?._id));
    }
  }, [data, setTemplateCategories]);


  // const goTo = useCallback((templateId) => {
  //   push(`/templates/manage/${templateId}`);
  // }, [push]);

  const handleSelect = useCallback((useTemplateId) => {
    // El template seleccionado es siempre un template original (sin parent)
    // En modo creación: se envía este template y el API crea una copia
    // En modo edición: se envía este template (el parent del template actual de la auditoría)
    setTemplateId(useTemplateId);
    onChange(useTemplateId);
  }, [onChange, setTemplateId]);

  useEffect(() => {
    if (serviceId && serviceId !== currentService) {
      handleSelect(null);
    }
    setServiceId(currentService);
  }, [currentService, handleSelect, serviceId, setServiceId]);

  return (
    <div className={classNames('w-full h-full')}>
      <div className={styles.header}>
        { t('templates') }
      </div>
      <Scrollbars
        style={{ height: 'calc(100vh - 300px)' }}
        renderTrackHorizontal={props => <div {...props} style={{display: 'none'}} className="track-horizontal"/>}
        disableHorizontalScrolling
      >
        <List
          style={{ marginTop: '5px' }}
          bordered
          dataSource={data}
          className="w-full"
          renderItem={item => {
            return (
              <List.Item
                className={classNames('w-full wrapper-template-list', styles.wrapper)}
                style={{
                  backgroundColor: templateId === item?._id ? '#E6F7FF' : '#fff',
                  borderLeft: item?.category?.color ? `6px solid ${item?.category?.color}` : item?.category?.name ? stringToHslColor(item?.category?.name, 50, 50) : '6px solid #ccc'
                }}
              >
                <div className="w-full flex items-start justify-center flex-col">
                  <div>
                    <Typography.Text className="text-primary cursor-pointer"
                      onClick={() => handleSelect(item?._id)/*goTo(item?._id)*/}>{item?.name}</Typography.Text>
                  </div>
                  <div>
                    {moment(item?.createdAt).format('ll')}
                  </div>
                </div>

                <div className="actions">
                  {templateId !== item?._id && <Button type="primary"
                    onClick={() => handleSelect(item?._id)}>{t('selectThisTemplate')}</Button>}
                </div>
              </List.Item>
            );
          }}
        />
      </Scrollbars>
    </div>
  );
};

TemplateList.propTypes = {
  entity: PropTypes.object,
  currentCategory: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
};

export default memo(TemplateList);

